import { useRef, useState } from 'react';

import type { DraggableEvent } from 'react-draggable';
import Draggable from 'react-draggable';
import {
  AddchartOutlined,
  ArrowDropDown,
  DeleteOutlined,
  DragIndicator,
  Launch,
  ListAltOutlined
} from '@/shared/ui/icons';

import { Box, Typography } from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import type { SymbolKey } from '@/entities/symbol';
import CustomCheckbox from '@/shared/ui/primitives/Checkbox';

import { useSymbolDragBounds } from '../hooks/useSymbolDragBounds';
import { formatSelectionLabel } from '../lib/formatSelectionLabel';
import { SELECTOR_MODES } from '../state/symbolSelector.slice';
import { CreateAppMenu } from './CreateAppMenu/CreateAppMenu';
import { SelectorButton } from './SelectorButton/SelectorButton';

const SELECTOR_BOX_SHADOW = '0 6px 8px rgba(255,255,255,0.15)';

type Position = { x: number; y: number };

interface SymbolSelectorToolbarProps {
  symbols: SymbolKey[];
  selectorMode: SELECTOR_MODES;
  position: Position;
  isInCatalog: boolean;
  isDashboardAppSelector: boolean;
  isVisible: boolean;
  onDrag: (e: DraggableEvent, data: Position) => void;
  onDraggingChange: (dragging: boolean) => void;
  onClear: () => void;
  onViewCatalog: () => void;
  onExport: () => void;
  onRequestExclusiveAction: (fn: () => void) => void;
  onAddToApp: () => void;
}

export const SymbolSelectorToolbar = ({
  symbols,
  selectorMode,
  position,
  isInCatalog,
  isDashboardAppSelector,
  isVisible,
  onDrag,
  onDraggingChange,
  onClear,
  onViewCatalog,
  onExport,
  onRequestExclusiveAction,
  onAddToApp
}: SymbolSelectorToolbarProps) => {
  const anchorRef = useRef<HTMLElement | null>(null);
  const [createAppAnchor, setCreateAppAnchor] = useState<HTMLElement | null>(null);
  const { dragBounds, setDragging } = useSymbolDragBounds(anchorRef);

  const handleDragStart = () => {
    setDragging(true);
    onDraggingChange(true);
  };

  const handleDragStop = () => {
    setDragging(false);
    onDraggingChange(false);
  };

  const handleCreateAppClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.currentTarget;
    onRequestExclusiveAction(() => {
      setCreateAppAnchor(prev => (prev ? null : target));
    });
  };

  return (
    <Draggable
      bounds={dragBounds}
      handle='div.cursor'
      nodeRef={anchorRef}
      position={position}
      onDrag={onDrag}
      onStart={handleDragStart}
      onStop={handleDragStop}
    >
      <Box
        ref={anchorRef}
        className='scroll--white'
        sx={{
          p: 1.5,
          pr: 1,
          backgroundColor:
            selectorMode === SELECTOR_MODES.FORWARD_CURVE
              ? 'gx.selectorForwardCurve'
              : 'gx.highlight',
          minWidth: 354,
          zIndex: Z_INDEX.priority,
          position: 'absolute',
          boxShadow: SELECTOR_BOX_SHADOW,
          display: isVisible ? 'flex' : 'none',
          flexDirection: 'column'
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', mb: '8px' }}>
          {selectorMode === SELECTOR_MODES.FORWARD_CURVE ? 'Curve Mode' : 'Item Mode'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-around'
          }}
        >
          <Box
            className='cursor'
            style={{ paddingRight: 8 }}
          >
            <Box sx={{ cursor: 'all-scroll', display: 'flex' }}>
              <DragIndicator />
            </Box>
          </Box>
          <Box
            sx={{ cursor: 'default', display: 'flex', flexDirection: 'row', alignItems: 'center' }}
          >
            <CustomCheckbox
              checked
              sx={{ cursor: 'default' }}
            />
            <Box
              sx={{
                cursor: 'default',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                px: 1,
                mr: 1
              }}
            >
              <Typography sx={{ fontSize: 12 }}>
                {formatSelectionLabel(symbols, selectorMode)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex' }}>
            <SelectorButton onClick={onClear}>
              <DeleteOutlined />
              <Typography variant='caption'>Clear selection</Typography>
            </SelectorButton>

            {!isDashboardAppSelector && (
              <>
                <SelectorButton
                  disabled={isInCatalog}
                  onClick={onViewCatalog}
                >
                  <ListAltOutlined />
                  <Typography variant='caption'>View in Catalog</Typography>
                </SelectorButton>
                <SelectorButton onClick={onExport}>
                  <Launch />
                  <Typography variant='caption'>Export selection</Typography>
                </SelectorButton>
                <SelectorButton
                  sx={{ justifyContent: 'space-between' }}
                  onClick={handleCreateAppClick}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <AddchartOutlined />
                    <Typography variant='caption'>Create dashboard app</Typography>
                  </Box>
                  <ArrowDropDown />
                </SelectorButton>
                <CreateAppMenu
                  containerEl={anchorRef.current}
                  open={Boolean(createAppAnchor)}
                  selectorMode={selectorMode}
                  onClose={() => {
                    setCreateAppAnchor(null);
                  }}
                />
              </>
            )}

            {isDashboardAppSelector && (
              <SelectorButton onClick={onAddToApp}>
                <ListAltOutlined />
                <Typography variant='caption'>Add to app</Typography>
              </SelectorButton>
            )}
          </Box>
        </Box>
      </Box>
    </Draggable>
  );
};
